import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type {
  AnnouncementContentPatch,
  AnnouncementDto,
  AnnouncementListFilters,
  AnnouncementsAdminClient,
  CreateAnnouncementInput,
  EligibleCampaignDto,
} from '@/api/announcements-admin-client';
import { useAnnouncementsAdminClient } from '@/api/announcements-admin-client-context';

// =============================================================================
// use-announcements-admin.ts — Hooks TanStack Query du composer d'annonces ADMIN (R15-COM-03).
// =============================================================================
// LECTURES : registre (filtres statut/campagne/période) + campagnes éligibles. WRITES : create / autosave
// draft / schedule / publish-now / edit-content / cancel — chaque mutation renvoie l'annonce résultante et
// invalide le registre (le nouvel état/date/nombreDestinataires doit s'y refléter). ZÉRO règle métier ici :
// l'autorité (RLS, transitions d'état, éligibilité, fenêtre de modification, fan-out) est l'API.

const ANNOUNCEMENTS_KEY = ['communication', 'announcements'] as const;

/** Clé de query du registre (inclut les filtres pour un cache par vue). */
export const announcementsListKey = (
  filters: AnnouncementListFilters | undefined,
): readonly unknown[] => [...ANNOUNCEMENTS_KEY, 'list', filters ?? {}];

/** Clé de query des campagnes éligibles. */
export const eligibleCampaignsKey = [
  ...ANNOUNCEMENTS_KEY,
  'eligible-campaigns',
] as const;

/** Registre des annonces (filtré serveur : statut / campagne / période). */
export function useAnnouncements(
  filters?: AnnouncementListFilters,
): UseQueryResult<readonly AnnouncementDto[], Error> {
  const client: AnnouncementsAdminClient = useAnnouncementsAdminClient();
  return useQuery<readonly AnnouncementDto[], Error>({
    queryKey: announcementsListKey(filters),
    queryFn: () => client.list(filters),
  });
}

/** Campagnes cibles éligibles (filtrage d'éligibilité serveur — l'UI liste). */
export function useEligibleCampaigns(): UseQueryResult<
  readonly EligibleCampaignDto[],
  Error
> {
  const client: AnnouncementsAdminClient = useAnnouncementsAdminClient();
  return useQuery<readonly EligibleCampaignDto[], Error>({
    queryKey: eligibleCampaignsKey,
    queryFn: () => client.eligibleCampaigns(),
  });
}

/** Invalide TOUT le registre (toutes vues filtrées confondues). Appelé après chaque write. */
function useInvalidateAnnouncements(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async (): Promise<void> => {
    await queryClient.invalidateQueries({
      queryKey: [...ANNOUNCEMENTS_KEY, 'list'],
    });
  };
}

/** Création d'un brouillon (titre + contenu + campagne). Invalide le registre. */
export function useCreateAnnouncement(): UseMutationResult<
  AnnouncementDto,
  Error,
  CreateAnnouncementInput
> {
  const client: AnnouncementsAdminClient = useAnnouncementsAdminClient();
  const invalidate = useInvalidateAnnouncements();
  return useMutation<AnnouncementDto, Error, CreateAnnouncementInput>({
    mutationKey: [...ANNOUNCEMENTS_KEY, 'create'],
    mutationFn: (input: CreateAnnouncementInput) => client.create(input),
    onSuccess: () => invalidate(),
  });
}

/** Variables de patch d'une annonce (id + patch partiel titre/contenu). */
export interface AnnouncementPatchVars {
  readonly announcementId: string;
  readonly patch: AnnouncementContentPatch;
}

/**
 * AUTOSAVE silencieux du brouillon (PATCH partiel). Invalide le registre pour refléter le titre édité.
 * « Silencieux » = aucun toast/redirect ici : l'écran ne rend rien de bruyant à chaque succès (DBU §1.4.7).
 */
export function useUpdateAnnouncementDraft(): UseMutationResult<
  AnnouncementDto,
  Error,
  AnnouncementPatchVars
> {
  const client: AnnouncementsAdminClient = useAnnouncementsAdminClient();
  const invalidate = useInvalidateAnnouncements();
  return useMutation<AnnouncementDto, Error, AnnouncementPatchVars>({
    mutationKey: [...ANNOUNCEMENTS_KEY, 'update-draft'],
    mutationFn: ({ announcementId, patch }: AnnouncementPatchVars) =>
      client.updateDraft(announcementId, patch),
    onSuccess: () => invalidate(),
  });
}

/** Variables de planification (id + échéance ISO 8601). */
export interface ScheduleAnnouncementVars {
  readonly announcementId: string;
  readonly scheduledFor: string;
}

/** Planification de l'envoi différé (Brouillon → Planifiee). Invalide le registre. */
export function useScheduleAnnouncement(): UseMutationResult<
  AnnouncementDto,
  Error,
  ScheduleAnnouncementVars
> {
  const client: AnnouncementsAdminClient = useAnnouncementsAdminClient();
  const invalidate = useInvalidateAnnouncements();
  return useMutation<AnnouncementDto, Error, ScheduleAnnouncementVars>({
    mutationKey: [...ANNOUNCEMENTS_KEY, 'schedule'],
    mutationFn: ({ announcementId, scheduledFor }: ScheduleAnnouncementVars) =>
      client.schedule(announcementId, scheduledFor),
    onSuccess: () => invalidate(),
  });
}

/** Envoi immédiat (Brouillon → Modifiable, fenêtre de modification ouverte). Invalide le registre. */
export function usePublishAnnouncementNow(): UseMutationResult<
  AnnouncementDto,
  Error,
  string
> {
  const client: AnnouncementsAdminClient = useAnnouncementsAdminClient();
  const invalidate = useInvalidateAnnouncements();
  return useMutation<AnnouncementDto, Error, string>({
    mutationKey: [...ANNOUNCEMENTS_KEY, 'publish-now'],
    mutationFn: (announcementId: string) => client.publishNow(announcementId),
    onSuccess: () => invalidate(),
  });
}

/** Édition inline pendant la fenêtre de modification (PATCH content). Invalide le registre. */
export function useEditAnnouncementContent(): UseMutationResult<
  AnnouncementDto,
  Error,
  AnnouncementPatchVars
> {
  const client: AnnouncementsAdminClient = useAnnouncementsAdminClient();
  const invalidate = useInvalidateAnnouncements();
  return useMutation<AnnouncementDto, Error, AnnouncementPatchVars>({
    mutationKey: [...ANNOUNCEMENTS_KEY, 'edit-content'],
    mutationFn: ({ announcementId, patch }: AnnouncementPatchVars) =>
      client.editContent(announcementId, patch),
    onSuccess: () => invalidate(),
  });
}

/** Annulation d'une annonce Planifiee (Planifiee → Annulee). Invalide le registre. */
export function useCancelAnnouncement(): UseMutationResult<
  AnnouncementDto,
  Error,
  string
> {
  const client: AnnouncementsAdminClient = useAnnouncementsAdminClient();
  const invalidate = useInvalidateAnnouncements();
  return useMutation<AnnouncementDto, Error, string>({
    mutationKey: [...ANNOUNCEMENTS_KEY, 'cancel'],
    mutationFn: (announcementId: string) => client.cancel(announcementId),
    onSuccess: () => invalidate(),
  });
}
